import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router';

import { useWalletAddress } from '@/hooks/useWalletAddress';
import { TopNav } from '@/components/TopNav';
import { BottomNavigation } from '@/components/BottomNavigation';
import { HistoryDrawer } from '@/components/HistoryDrawer';
import { FaqDrawer } from '@/components/FaqDrawer';
import { PrizeTiersDrawer } from '@/components/PrizeTiersDrawer';
import { SettingsSheet } from '@/components/SettingsSheet';
import { InvitationRequiredModal } from '@/components/InvitationRequiredModal';
import { InvitationCodeModal } from '@/components/InvitationCodeModal';
import { RiveDumpster } from '@/components/RiveDumpster';
import { ChestOpeningModal } from '@/components/ChestOpeningModal';
import { ChestBackpackBonusIndicator } from '@/components/ChestBackpackBonusIndicator';
import { ChestGateBonusIndicator } from '@/components/ChestGateBonusIndicator';
import { ChestMagicEdenBonusIndicator } from '@/components/ChestMagicEdenBonusIndicator';
import { ChestBreakpointWinnerBonusIndicator } from '@/components/ChestBreakpointWinnerBonusIndicator';
import { ChestCflBonusIndicator } from '@/components/ChestCflBonusIndicator';
import { ChestSmbMatricaBonusIndicator } from '@/components/ChestSmbMatricaBonusIndicator';
import { ChestMattleBonusIndicator } from '@/components/ChestMattleBonusIndicator';
import { EmbeddedWalletWarningModal } from '@/components/EmbeddedWalletWarningModal';
import { useUserProgress } from '@/hooks/useUserProgress';
import { useOpenChest } from '@/hooks/useOpenChest';
import { usePhantom } from '@/context/PhantomContext';
import { useReferral } from '@/hooks/useReferral';
import { useResponsive } from '@/hooks/useResponsive';
import { useShare } from '@/hooks/useShare';
import { formatSolBalance } from '@/utils/solana/lamportsToSol';

// Chest Count Selector Component
function ChestCountSelector({
  chestCredits,
  chestPrice,
  chestCount,
  onChestCountChange,
}: {
  chestCredits: string;
  chestPrice: string;
  chestCount: number;
  onChestCountChange: (count: number) => void;
}) {
  // Calculate max chests from credits (not SOL balance)
  const maxChests = chestPrice !== '0' && BigInt(chestPrice) > 0
    ? Number(BigInt(chestCredits) / BigInt(chestPrice))
    : 0;
  const sliderPercentage = maxChests > 0 ? Math.min(100, Math.max(0, (chestCount / maxChests) * 100)) : 0;
  const filledWidth = maxChests > 0 ? Math.min(100, Math.max(0, sliderPercentage)) : 0;

  // Calculate cost for display - use formatSolBalance to avoid rounding up
  const costInCredits = chestPrice !== '0' ? formatSolBalance(BigInt(chestPrice) * BigInt(chestCount)) : '0.0000';

  useEffect(() => {
    if (chestCount > maxChests && maxChests > 0) {
      onChestCountChange(maxChests);
    } else if (chestCount < 0) {
      onChestCountChange(0);
    }
  }, [maxChests, chestCount, onChestCountChange]);

  const handleSliderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newCount = Number(event.target.value);
    onChestCountChange(newCount);
  };

  const handleSliderInput = (event: React.FormEvent<HTMLInputElement>) => {
    const newCount = Number((event.target as HTMLInputElement).value);
    onChestCountChange(newCount);
  };

  const [inputValue, setInputValue] = useState<string>(chestCount.toString());

  // Sync input value with chestCount when it changes externally (e.g., from slider)
  useEffect(() => {
    setInputValue(chestCount.toString());
  }, [chestCount]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    // Allow empty string - don't update chestCount yet
    if (value === '') {
      setInputValue('');
      return;
    }
    const numValue = Number(value);
    if (!Number.isNaN(numValue) && numValue >= 0) {
      const clampedValue = Math.min(maxChests || 0, Math.max(0, Math.floor(numValue)));
      setInputValue(value); // Keep user's input
      onChestCountChange(clampedValue);
    }
  };

  const handleInputBlur = () => {
    // If input is empty on blur, set to 0
    if (inputValue === '') {
      setInputValue('0');
      onChestCountChange(0);
    }
  };

  return (
    <div className="space-y-4" style={{ position: 'relative', zIndex: 10 }}>
      {/* Campaign Bonus Indicators */}
      <div className="flex justify-start gap-2 flex-wrap">
        <ChestBreakpointWinnerBonusIndicator />
        <ChestBackpackBonusIndicator />
        <ChestMagicEdenBonusIndicator />
        <ChestGateBonusIndicator />
        <ChestCflBonusIndicator />
        <ChestSmbMatricaBonusIndicator />
        <ChestMattleBonusIndicator />
      </div>

      {/* Condensed Slider with Input - All in dotted border */}
      <div className="flex items-center gap-3 px-4 py-2" style={{
        border: '1px dotted #50E4F9',
        borderRadius: '9999px',
        position: 'relative',
        zIndex: 10,
      }}>
        {/* Slider */}
        <div className="flex-1">
          <input
            type="range"
            min="0"
            max={maxChests || 0}
            value={chestCount}
            onChange={handleSliderChange}
            onInput={handleSliderInput}
            className="w-full h-3 rounded-full appearance-none cursor-pointer slider"
            style={{
              backgroundColor: '#FFFFFF',
              backgroundImage:
                'linear-gradient(92deg, #FB8B00 -4.02%, #F6DF6F 24.3%, #FB8B00 50.55%, #F6DF6F 72.17%, #FB8B00 103.05%)',
              backgroundSize: `${filledWidth}% 100%`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'left center',
              borderRadius: '9999px',
              height: '10px',
              pointerEvents: 'auto',
              transition: 'none',
            }}
          />
        </div>

        {/* Input Field */}
        <input
          type="number"
          step="1"
          min="0"
          max={maxChests || 0}
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          className="w-16 px-2 py-1 rounded-lg text-center"
          style={{
            backgroundColor: '#343434',
            border: '2px solid #50E5F8',
            color: '#FFF',
            fontFamily: 'Outfit, sans-serif',
            fontWeight: 'bold',
            fontSize: '14px',
          }}
          placeholder="0"
        />

        {/* Label */}
        <span className="text-white font-semibold whitespace-nowrap" style={{ fontFamily: 'Outfit, sans-serif', fontSize: '14px' }}>
          Chests
        </span>
      </div>

      {/* Total Cost Display */}
      {chestCount > 0 && (
        <div className="flex justify-end">
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg"
            style={{
              backgroundColor: 'rgba(254, 187, 1, 0.12)',
              border: '1px solid rgba(254, 187, 1, 0.8)',
            }}
          >
            <span className="text-xs" style={{ fontFamily: 'Outfit, sans-serif', color: 'rgba(255, 255, 255, 0.6)' }}>
              Cost:
            </span>
            <span style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 'bold', fontSize: '16px', color: '#FEBB01' }}>
              {costInCredits} Credits
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// Large chest SVG component
function ChestEmoji({ isOpening = false, isMobile }: { isOpening?: boolean; isMobile: boolean }) {
  const base = {
    containerWidth: 600,
    containerHeight: 450,
    raccoonWidth: 220,
    raccoonTop: -30,
    raccoonOffset: -20,
    shadowBottom: -15,
    shadowWidth: 850,
    shadowScale: 1.5,
    dumpsterTop: -80,
    dumpsterWidth: 650,
    dumpsterHeight: 600,
  };

  // Apply additional 30% scale reduction for mobile (0.7x of existing scale)
  const containerScale = isMobile ? 0.75 * 0.7 : 1;
  const raccoonScale = isMobile ? 0.55 * 0.7 : 1;
  const shadowWidthScale = isMobile ? 1.25 * 0.7 : 1;
  const adjustedShadowScale = isMobile ? base.shadowScale * 1.2 * 0.7 : base.shadowScale;
  const dumpsterScale = isMobile ? 0.7 * 0.7 : 1;
  
  // Mobile-only adjustments: move raccoons down and closer to center
  const raccoonTopAdjustment = isMobile ? 70 : 0; // Reduced from 100px to 70px (moved up 30px)
  const raccoonOffsetAdjustment = isMobile ? 40 : 0; // Move 40px closer to center on mobile
  
  // Mobile-only adjustments for dumpster and shadow
  const dumpsterTopAdjustment = isMobile ? 30 : 0; // Reduced from 60px to 30px (moved up 30px)
  const shadowBottomAdjustment = isMobile ? -30 : 0; // Reduced from -60 to -30 (moved up 30px)

  return (
    <div className="flex justify-center" style={{ marginBottom: isMobile ? '80px' : '16px', marginTop: isMobile ? '-48px' : '-96px', pointerEvents: 'none' }}>
      <div
        style={{
          position: 'relative',
          width: `${base.containerWidth * containerScale}px`,
          height: `${base.containerHeight * containerScale}px`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'visible',
          pointerEvents: 'none',
        }}
      >
        <img
          src="/Left Raccoon.svg"
          alt="Left Raccoon"
          style={{
            position: 'absolute',
            top: `${base.raccoonTop + raccoonTopAdjustment}px`,
            left: `${base.raccoonOffset + raccoonOffsetAdjustment}px`,
            width: `${base.raccoonWidth * raccoonScale}px`,
            height: 'auto',
            zIndex: 0,
          }}
        />

        <img
          src="/Right Raccoon.svg"
          alt="Right Raccoon"
          style={{
            position: 'absolute',
            top: `${base.raccoonTop + raccoonTopAdjustment}px`,
            right: `${base.raccoonOffset + raccoonOffsetAdjustment}px`,
            width: `${base.raccoonWidth * raccoonScale}px`,
            height: 'auto',
            zIndex: 0,
          }}
        />

        <img
          src="/Generative Fill@2x.svg"
          alt="Shadow"
          style={{
            position: 'absolute',
            bottom: `${(base.shadowBottom * containerScale) + shadowBottomAdjustment}px`,
            left: '50%',
            transform: `translateX(-50%) scale(${adjustedShadowScale})`,
            width: `${base.shadowWidth * shadowWidthScale}px`,
            height: 'auto',
            zIndex: 0,
          }}
        />

        <div style={{ zIndex: 100, position: 'relative', top: `${(base.dumpsterTop * containerScale) + dumpsterTopAdjustment}px` }}>
          <RiveDumpster
            width={base.dumpsterWidth * dumpsterScale}
            height={base.dumpsterHeight * dumpsterScale}
            isOpen={isOpening}
          />
        </div>
      </div>
    </div>
  );
}

export default function Chest() {
  const { isConnected } = useWalletAddress();
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const { isEmbeddedWallet } = usePhantom();

  const [chestCount, setChestCount] = useState(0);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [faqOpen, setFaqOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [prizeTiersOpen, setPrizeTiersOpen] = useState(false);
  const [showInviteRequired, setShowInviteRequired] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [isChestOpening, setIsChestOpening] = useState(false);
  const [showEmbeddedWarning, setShowEmbeddedWarning] = useState(false);

  // Processing state for loading modal
  const [processingStep, setProcessingStep] = useState<'signing' | 'sending' | 'processing' | 'confirming'>('signing');
  const [pollingAttempt, setPollingAttempt] = useState(0);
  const [showOpeningModal, setShowOpeningModal] = useState(false);

  const [showChestSuccess, setShowChestSuccess] = useState(false);
  const [lastChestResult, setLastChestResult] = useState<{
    signature: string;
    chestsOpened: number;
    totalPayout: bigint;
    totalPayoutSol: number;
    rewards: Array<{ tierIndex: number; amount: bigint; amountSol: number }>;
    tierPointsEarned: number;
    pointRewards: Array<{ tierIndex: number; amount: number }>;
  }>({
    signature: '',
    chestsOpened: 0,
    totalPayout: BigInt(0),
    totalPayoutSol: 0,
    rewards: [],
    tierPointsEarned: 0,
    pointRewards: [],
  });

  const { userProgress, isLoading: progressLoading, invalidate, refetch } = useUserProgress();
  const { mutate: openChest, isPending: isOpening } = useOpenChest();
  const { hasInviter } = useReferral();
  const { shareChest, getPotentialPoints, isSharing } = useShare();

  // Calculate points earned per chest
  // Base: 500 points (OPEN_CHEST)
  // If has inviter: +200 points (INVITEE_OPEN_CHEST)
  // Tier points are added from the actual tier draws
  const basePointsPerChest = 500;
  const inviteeBonus = hasInviter ? 200 : 0;

  // Set initial chest count based on available credits
  useEffect(() => {
    if (userProgress) {
      // Use available_chests calculated in the hook
      const maxChests = userProgress.available_chests || 0;
      // Default to 1 if user has enough for at least one chest, otherwise 0
      setChestCount(maxChests >= 1 ? 1 : 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProgress?.available_chests]);

  // Actual chest opening logic (called after confirmation for embedded wallets)
  const executeOpenChest = async () => {
    if (!userProgress) {
      return;
    }

    // Trigger opening animation immediately for better UX
    setIsChestOpening(true);
    setShowOpeningModal(true);
    setProcessingStep('signing');
    setPollingAttempt(0);

    // CRITICAL: Fetch fresh balance from on-chain before validation
    const { data: freshUserProgress } = await refetch();

    if (!freshUserProgress) {
      toast.error('Failed to verify balance. Please try again.');
      setIsChestOpening(false);
      setShowOpeningModal(false);
      return;
    }

    // Use fresh data for validation
    const currentBalance = freshUserProgress;

    if (chestCount < 1) {
      toast.error('Please select at least 1 chest to open');
      setIsChestOpening(false);
      setShowOpeningModal(false);
      return;
    }

    // Use chest credits (from burning) not SOL balance (from PDA)
    const chestCredits = BigInt(currentBalance.chest_credits);
    const chestPrice = BigInt(currentBalance.chest_price);
    const availableChests = currentBalance.available_chests;
    const requiredCredits = chestPrice * BigInt(chestCount);

    // Use credits-based validation
    if (chestCredits < requiredCredits) {
      toast.error(
        `Insufficient Credits. You have ${availableChests} chests available but are trying to open ${chestCount} chests. Please burn tokens or deposit Credits first.`,
        { duration: 6000 }
      );
      setIsChestOpening(false);
      setShowOpeningModal(false);
      return;
    }

    // Call useOpenChest hook mutation - calls backend for co-signing
    openChest(
      {
        chestCount,
        onProgress: (step, attempt) => {
          setProcessingStep(step);
          if (attempt !== undefined) {
            setPollingAttempt(attempt);
          }
        }
      },
      {
        onSuccess: (data) => {
          // Hide loading modal
          setShowOpeningModal(false);

          // Show success modal with parsed rewards from events
          setLastChestResult({
            signature: data.signatures[0] || '', // Use first signature from array
            chestsOpened: data.chestsOpened,
            totalPayout: data.totalPayout,
            totalPayoutSol: data.totalPayoutSol,
            rewards: data.rewards,
            tierPointsEarned: data.tierPointsEarned,
            pointRewards: data.pointRewards,
          });
          setShowChestSuccess(true);

          // Invalidate user progress to refresh balance
          invalidate();

          // Reset opening state after animation completes
          setTimeout(() => setIsChestOpening(false), 1500);
        },
        onError: () => {
          // Reset opening state on error
          setIsChestOpening(false);
          setShowOpeningModal(false);
        },
      }
    );
  };

  const handleOpenChest = async () => {
    // INVITATION REQUIREMENT DISABLED
    // Uncomment the block below to re-enable invitation-only mode:
    // if (!checkAccess()) {
    //   setShowInviteRequired(true);
    //   return;
    // }

    if (!userProgress) {
      return;
    }

    // Show warning modal for embedded wallet users
    if (isEmbeddedWallet) {
      setShowEmbeddedWarning(true);
      return;
    }

    // For regular wallets, proceed directly
    executeOpenChest();
  };

  // Handle embedded wallet warning confirmation
  const handleEmbeddedWarningConfirm = () => {
    setShowEmbeddedWarning(false);
    executeOpenChest();
  };

  // Show connect prompt if wallet not connected or loading
  if (!isConnected || progressLoading || !userProgress) {
    return (
      <div className="min-h-screen" style={{ position: 'relative' }}>
        <TopNav
          onHistoryClick={() => setHistoryOpen(true)}
          onFaqClick={() => setFaqOpen(true)}
          onPrizeTiersClick={() => setPrizeTiersOpen(true)}
          onSettingsClick={() => setSettingsOpen(true)}
          onNFTGalleryClick={() => navigate('/my-nft')}
        />

        <div className="content-container-with-noise px-4 py-8 flex items-center justify-center" style={{ overflow: 'visible' }}>
            <div className="text-center">
              {!isConnected ? (
                <>
                  <ChestEmoji isMobile={isMobile} />
                  <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Connect Your Wallet</h2>
                  <p className="text-muted-foreground">
                    Connect your wallet to view your Credits balance and open treasure chests
                  </p>
                </>
              ) : progressLoading ? (
                <>
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
                  <div className="text-muted-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>Loading...</div>
                </>
              ) : (
                <>
                  <ChestEmoji isMobile={isMobile} />
                  <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Loading Balance...</h2>
                  <p className="text-muted-foreground">
                    Please wait while we load your balance
                  </p>
                </>
              )}
            </div>
          </div>

        <BottomNavigation />

        <HistoryDrawer open={historyOpen} onClose={() => setHistoryOpen(false)} />
        <FaqDrawer open={faqOpen} onClose={() => setFaqOpen(false)} />
        <PrizeTiersDrawer open={prizeTiersOpen} onClose={() => setPrizeTiersOpen(false)} />
        <SettingsSheet open={settingsOpen} onClose={() => setSettingsOpen(false)} />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ position: 'relative' }}>
      <TopNav
        onHistoryClick={() => setHistoryOpen(true)}
        onFaqClick={() => setFaqOpen(true)}
        onPrizeTiersClick={() => setPrizeTiersOpen(true)}
        onSettingsClick={() => setSettingsOpen(true)}
        onNFTGalleryClick={() => navigate('/my-nft')}
      />

      {/* Side Buttons - positioned outside content area - Hidden on mobile */}
      <div className="hidden md:flex fixed right-8 top-1/2 -translate-y-1/2 flex-col gap-6 z-40">
        <button
          onClick={() => setPrizeTiersOpen(true)}
          className="transition-all hover:opacity-70"
        >
          <img src="/rules.svg" alt="Rules" width={72} height={72} />
        </button>
      </div>

      <div className="content-container-with-noise px-4 py-8 pb-24" style={{ overflow: 'visible' }}>
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Chest Content */}
          <div className="space-y-6">
              {/* Chest Display */}
              <div className="text-center">
            <div className="mb-0">
              <h1
                style={{
                  color: '#FFF',
                  textAlign: 'center',
                  textShadow: '0 4px 10.4px rgba(0, 0, 0, 0.50)',
                  fontFamily: 'Hyperspace Race Capsule',
                  fontSize: isMobile ? '32px' : '64px',
                  fontStyle: 'normal',
                  fontWeight: 900,
                  lineHeight: '1.2',
                  textTransform: 'uppercase',
                  marginBottom: '12px',
                  whiteSpace: 'nowrap',
                }}
              >
                OPEN DUMPSTERS
              </h1>
              <h2
                className="mb-4"
                style={{
                  color: '#50E4F9',
                  textAlign: 'center',
                  textShadow: '0 4px 10.4px rgba(0, 0, 0, 0.50)',
                  fontFamily: 'Hyperspace Race Capsule',
                  fontSize: isMobile ? '28px' : '48px',
                  fontStyle: 'normal',
                  fontWeight: 900,
                  lineHeight: '1.2',
                  textTransform: 'uppercase',
                  marginTop: '0',
                }}
              >
                WIN <span style={{ color: '#FEBB01' }}>POINTS</span> AND <span style={{ color: '#9548FC' }}>SOL</span>
              </h2>

              {/* Animated Arrow - Scroll to Open Section */}
              {/* OPEN ME button with arrow - hidden on mobile */}
              {!isMobile && (
                <button
                  onClick={() => {
                    const openSection = document.getElementById('chest-controls');
                    openSection?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }}
                  className="mb-0"
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    animation: 'bounce 2s infinite',
                    position: 'relative',
                    zIndex: 150, // Higher than dumpster (which is 100)
                    pointerEvents: 'auto',
                  }}
                >
                  <div className="flex flex-col items-center gap-2">
                    <span
                      style={{
                        fontFamily: 'Hyperspace Race Capsule, cursive',
                        fontSize: '20px',
                        color: '#50E5F8',
                        textShadow: '0 2px 8px rgba(80, 229, 248, 0.5)',
                      }}
                    >
                      OPEN ME
                    </span>
                    <img 
                      src="/arrowraccoon.png" 
                      alt="Down Arrow" 
                      style={{
                        width: '120px',
                        height: '120px',
                        objectFit: 'contain',
                      }}
                    />
                  </div>
                </button>
              )}

              <style>{`
                @keyframes bounce {
                  0%, 100% {
                    transform: translateY(0);
                  }
                  50% {
                    transform: translateY(-10px);
                  }
                }
              `}</style>
            </div>

            <ChestEmoji isOpening={isChestOpening} isMobile={isMobile} />

            {/* Show message if user has insufficient Credits */}
            {userProgress.available_chests < 1 && (
              <div className="mt-6 text-center">
                <div
                  style={{
                    fontFamily: 'Hyperspace Race Capsule, cursive',
                    fontSize: '32px',
                    color: '#EF4444',
                    fontWeight: 'bold',
                    marginBottom: '8px',
                  }}
                >
                  INSUFFICIENT CREDITS
                </div>
                <div
                  style={{
                    fontFamily: 'Outfit, sans-serif',
                    fontSize: '14px',
                    color: 'rgba(255, 255, 255, 0.7)',
                  }}
                >
                  PLEASE GO TO JUNK OR DEPOSIT FIRST.
                </div>
              </div>
            )}
          </div>

          {/* Chest Count Selection Controls */}
          <div id="chest-controls">
            <div className="relative">
              <ChestCountSelector
                chestCredits={userProgress.chest_credits}
                chestPrice={userProgress.chest_price}
                chestCount={chestCount}
                onChestCountChange={setChestCount}
              />

              {/* Info Icon with Tooltip */}
              <div className="absolute -top-8 right-0 group" style={{ zIndex: 9999 }}>
                <div className="relative">
                  {/* Info Icon */}
                  <div
                    className="cursor-help text-white/60 hover:text-white transition-colors"
                    style={{ fontSize: '20px' }}
                  >
                    ⓘ
                  </div>

                  {/* Tooltip */}
                  <div className="absolute right-0 bottom-full mb-2 w-72 p-4 bg-[#2A2A2A] border border-[#50E5F8] rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200" style={{ zIndex: 10000 }}>
                    <div className="relative">
                      {/* Tooltip Arrow */}
                      <div className="absolute -bottom-6 right-2 w-0 h-0 border-l-[8px] border-l-transparent border-r-[8px] border-r-transparent border-t-[8px] border-t-[#50E5F8]"></div>

                      <div style={{ fontFamily: 'Outfit, sans-serif' }}>
                        <h4 className="text-white font-bold text-base mb-2">How Chest Opening Works</h4>
                        <ul className="text-white/80 text-sm space-y-1.5">
                          <li>• You have <span className="text-[#FEBB01] font-semibold">{formatSolBalance(BigInt(userProgress.chest_credits))} Credits</span></li>
                          <li>• Each chest costs <span className="text-[#FEBB01] font-semibold">{formatSolBalance(BigInt(userProgress.chest_price))} Credits</span></li>
                          <li>• You can open up to <span className="text-[#50E5F8] font-semibold">{userProgress.available_chests}</span> chest{userProgress.available_chests !== 1 ? 's' : ''} right now</li>
                          <li className="pt-1 border-t border-white/10">• Earn more credits by burning junk tokens</li>
                          <li>• Open chests to win prizes and rewards!</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Open Chest Button */}
          <div className="flex justify-center">
            <button
              onClick={handleOpenChest}
              disabled={isOpening || userProgress.available_chests < 1}
              className="py-3 px-8 disabled:opacity-50 disabled:cursor-not-allowed text-black font-bold transition-all text-xl transform hover:scale-105"
              style={{
                fontFamily: 'Hyperspace Race Capsule, cursive',
                borderRadius: '100px',
                background: 'linear-gradient(92deg, #FB8B00 -4.02%, #F6DF6F 24.3%, #FB8B00 50.55%, #F6DF6F 72.17%, #FB8B00 103.05%)',
                boxShadow: '4px 4px 12px 0 rgba(0, 0, 0, 0.50)',
              }}
            >
              {isOpening ? 'OPENING CHEST...' : 'OPEN CHEST'}
            </button>
          </div>

          {/* Opening requirement note */}
          <div className="text-center">
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-white text-sm border-2"
              style={{ borderColor: '#FFFFFF' }}
            >
              <div
                className="flex items-center justify-center rounded-full border-2"
                style={{
                  width: '24px',
                  height: '24px',
                  borderColor: '#FFFFFF',
                  backgroundColor: 'transparent',
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <path
                    d="M2.55571 3.40137L2.10673 6.7874H3.11951V3.40137H2.55571ZM3.61583 3.40137V6.7874H4.66363V3.40137H3.61583ZM5.15995 3.40137V6.7874H6.72243V6.3021H9.27762V6.7874H10.8401V3.40137H5.15995ZM11.3364 3.40137V6.7874H12.3842V3.40137H11.3364ZM12.8805 3.40137V6.7874H13.8933L13.4443 3.40137H12.8805ZM7.21876 6.79843V9.69916H8.78129V6.79843H7.21876ZM8.00002 7.11811C8.13651 7.11812 8.26741 7.16489 8.36393 7.24814C8.46045 7.33139 8.51469 7.4443 8.51471 7.56205C8.51461 7.64145 8.48983 7.71937 8.44296 7.7877C8.39608 7.85603 8.32882 7.91227 8.24819 7.95056V8.97668H7.75186V7.951C7.67115 7.91268 7.60384 7.85637 7.55696 7.78795C7.51008 7.71954 7.48534 7.64152 7.48534 7.56205C7.48536 7.4443 7.5396 7.33139 7.63612 7.24814C7.73264 7.16489 7.86354 7.11812 8.00002 7.11811ZM2.07172 7.28372V10.8108L2.60482 11.3135V7.28372H2.07172ZM3.10115 7.28372V11.6403H12.8989V7.28372H9.27762V10.1955H6.72243V7.28372H3.10115ZM13.3952 7.28372V11.3135L13.9283 10.8108V7.28372H13.3952ZM2.07172 11.493V12.6109H3.25743L2.07172 11.493V11.493ZM13.9283 11.493L12.7426 12.6109H13.9283V11.493V11.493ZM3.47797 12.1367L3.98091 12.6109H12.0191L12.5221 12.1367H3.47797Z"
                    fill="white"
                  />
                </svg>
              </div>
              <span style={{ fontFamily: 'Outfit, sans-serif' }}>
                Opening a chest requires {formatSolBalance(BigInt(userProgress.chest_price))} Credits
              </span>
            </div>
          </div>
        </div>
      </div>
      </div>

      <BottomNavigation />

      {/* Drawers */}
      <HistoryDrawer open={historyOpen} onClose={() => setHistoryOpen(false)} />
      <FaqDrawer open={faqOpen} onClose={() => setFaqOpen(false)} />
      <PrizeTiersDrawer open={prizeTiersOpen} onClose={() => setPrizeTiersOpen(false)} />
      <SettingsSheet open={settingsOpen} onClose={() => setSettingsOpen(false)} />

      {/* Invitation Modals */}
      <InvitationRequiredModal
        open={showInviteRequired}
        onClose={() => setShowInviteRequired(false)}
        onOpenInviteModal={() => setShowInviteModal(true)}
        actionName="open chests"
      />
      <InvitationCodeModal
        open={showInviteModal}
        onClose={() => setShowInviteModal(false)}
      />

      {/* Embedded Wallet Warning Modal */}
      <EmbeddedWalletWarningModal
        isOpen={showEmbeddedWarning}
        onConfirm={handleEmbeddedWarningConfirm}
        onCancel={() => setShowEmbeddedWarning(false)}
        chestCount={chestCount}
        creditsRequired={formatSolBalance(BigInt(userProgress.chest_price) * BigInt(chestCount))}
      />

      {/* Chest Opening Loading Modal */}
      <ChestOpeningModal
        isOpen={showOpeningModal}
        chestCount={chestCount}
        processingStep={processingStep}
        attemptCount={pollingAttempt}
        maxAttempts={30}
        isEmbeddedWallet={isEmbeddedWallet}
      />

      {/* Chest Success Modal */}
      {showChestSuccess && lastChestResult.signature && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex flex-col items-center justify-center z-50">
          <div className="relative w-full h-full flex flex-col">
            {/* Top Gray Bar with Congratulations - Full Width */}
            <div 
              className="py-6 px-8 text-center"
              style={{
                backgroundColor: 'rgba(40, 40, 40, 0.95)',
                borderTop: '3px solid #50E4F9',
                borderBottom: '3px solid #50E4F9',
              }}
            >
              <h1
                className="text-5xl md:text-6xl font-bold"
                style={{
                  fontFamily: 'Outfit, sans-serif',
                  color: '#FFFFFF',
                }}
              >
                CONGRATULATIONS
              </h1>
            </div>

            {/* Middle Content Area with Video Background */}
            <div className="relative flex-1 flex items-center justify-center overflow-hidden">
              {/* Background Video */}
              <video 
                autoPlay 
                loop 
                muted 
                playsInline
                className="absolute inset-0 w-full h-full object-contain"
                style={{ 
                  filter: 'brightness(0.5)',
                  opacity: 0.8,
                  transform: 'scale(1.5)',
                }}
              >
                <source src="/chestgif.mp4" type="video/mp4" />
              </video>

              {/* Content Container */}
              <div className="relative z-10 w-full max-w-6xl px-8">
                {/* Chest Rewards Section */}
                {lastChestResult.rewards.length > 0 ? (
                  <div className="mb-6">
                    <h2
                      className="text-2xl md:text-3xl text-center mb-4"
                      style={{
                        fontFamily: 'Outfit, sans-serif',
                        fontWeight: 700,
                        color: '#FFFFFF',
                      }}
                    >
                      Chest Rewards
                    </h2>

                    {/* Horizontal scrollable container */}
                    <div className="overflow-x-auto pb-4">
                      <div className="flex gap-4" style={{ minWidth: 'min-content' }}>
                        {lastChestResult.rewards.map((reward, index) => (
                          <div
                            key={index}
                            className="p-4 rounded-xl flex flex-col items-center justify-center flex-shrink-0"
                            style={{
                              backgroundColor: 'rgba(80, 228, 249, 0.1)',
                              border: '2px solid #50E4F9',
                              minWidth: '180px',
                            }}
                          >
                            <div className="w-12 h-12 mb-2 flex items-center justify-center rounded-full" style={{ backgroundColor: 'rgba(80, 228, 249, 0.2)' }}>
                              <img
                                src="/solana-sol-logo.svg"
                                alt="Credits"
                                className="w-8 h-8"
                              />
                            </div>
                            <div
                              className="text-2xl font-bold"
                              style={{
                                fontFamily: 'Outfit, sans-serif',
                                fontWeight: 700,
                                color: '#FFFFFF',
                              }}
                            >
                              {formatSolBalance(reward.amount)} Credits
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="mb-6 text-center py-8">
                    <p
                      className="text-2xl md:text-3xl text-white"
                      style={{ 
                        fontFamily: 'Outfit, sans-serif',
                        fontWeight: 400,
                      }}
                    >
                      Didn't win credits this time. Better Luck Next Time!
                    </p>
                  </div>
                )}

                {/* Referral, Chest Rewards, and Points Earned */}
                <div className={`grid grid-cols-1 ${hasInviter ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-4`}>
                  {/* Referral Rewards - Only show if user has inviter */}
                  {hasInviter && (
                    <div className="text-center">
                      <h3
                        className="text-lg md:text-xl mb-3"
                        style={{
                          fontFamily: 'Outfit, sans-serif',
                          fontWeight: 700,
                          color: '#FFFFFF',
                        }}
                      >
                        Referral Bonus
                      </h3>
                      <div
                        className="p-4 rounded-xl flex flex-col items-center justify-center min-h-[140px]"
                        style={{
                          backgroundColor: 'rgba(251, 139, 0, 0.1)',
                          border: '2px solid #FB8B00',
                        }}
                      >
                        <img
                          src="/points.svg"
                          alt="Points"
                          className="w-12 h-12 mb-2"
                        />
                        <div
                          className="text-xl font-bold"
                          style={{
                            fontFamily: 'Outfit, sans-serif',
                            fontWeight: 700,
                            color: '#FFFFFF',
                          }}
                        >
                          {inviteeBonus * lastChestResult.chestsOpened} Points
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Chest Rewards - What you won from opening chests */}
                  <div className="text-center">
                    <h3
                      className="text-lg md:text-xl mb-3"
                      style={{
                        fontFamily: 'Outfit, sans-serif',
                        fontWeight: 700,
                        color: '#FFFFFF',
                      }}
                    >
                      Chest Rewards
                    </h3>
                    <div
                      className="p-4 rounded-xl flex flex-col items-center justify-center min-h-[140px]"
                      style={{
                        backgroundColor: 'rgba(251, 139, 0, 0.1)',
                        border: '2px solid #FB8B00',
                      }}
                    >
                      <img
                        src="/points.svg"
                        alt="Points"
                        className="w-12 h-12 mb-2"
                      />
                      <div
                        className="text-xl font-bold"
                        style={{
                          fontFamily: 'Outfit, sans-serif',
                          fontWeight: 700,
                          color: '#FFFFFF',
                        }}
                      >
                        {lastChestResult.tierPointsEarned || 0} Points
                      </div>
                      <div
                        className="text-sm mt-1"
                        style={{
                          fontFamily: 'Outfit, sans-serif',
                          color: 'rgba(255, 255, 255, 0.7)',
                        }}
                      >
                        From tier draws
                      </div>
                    </div>
                  </div>

                  {/* Points Earned - Total with Detailed Breakdown */}
                  <div className="text-center">
                    <h3
                      className="text-lg md:text-xl mb-3"
                      style={{
                        fontFamily: 'Outfit, sans-serif',
                        fontWeight: 700,
                        color: '#FFFFFF',
                      }}
                    >
                      Total Points Earned
                    </h3>
                    <div
                      className="p-4 rounded-xl flex flex-col items-center justify-center min-h-[140px]"
                      style={{
                        backgroundColor: 'rgba(251, 139, 0, 0.1)',
                        border: '2px solid #FB8B00',
                      }}
                    >
                      <img
                        src="/points.svg"
                        alt="Points"
                        className="w-12 h-12 mb-2"
                      />
                      <div
                        className="text-2xl font-bold mb-2"
                        style={{
                          fontFamily: 'Outfit, sans-serif',
                          fontWeight: 700,
                          color: '#FEBB01',
                        }}
                      >
                        {(basePointsPerChest + inviteeBonus) * lastChestResult.chestsOpened + (lastChestResult.tierPointsEarned || 0)} PTS
                      </div>
                      <div
                        className="text-xs space-y-1"
                        style={{
                          fontFamily: 'Outfit, sans-serif',
                          color: 'rgba(255, 255, 255, 0.85)',
                        }}
                      >
                        <div>Base Activity: {basePointsPerChest * lastChestResult.chestsOpened} pts</div>
                        {hasInviter && <div>Referral Bonus: {inviteeBonus * lastChestResult.chestsOpened} pts</div>}
                        {(lastChestResult.tierPointsEarned || 0) > 0 && <div>Tier Rewards: {lastChestResult.tierPointsEarned} pts</div>}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Gray Bar with Action Buttons - Full Width */}
            <div 
              className="py-6 px-8"
              style={{
                backgroundColor: 'rgba(40, 40, 40, 0.95)',
                borderTop: '3px solid #50E4F9',
              }}
            >
              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-4">
                <button
                  onClick={() => {
                    setShowChestSuccess(false);
                    navigate('/rewards');
                  }}
                  className="w-full sm:w-auto py-4 px-12 font-bold transition-all text-xl transform hover:scale-105"
                  style={{
                    fontFamily: 'Outfit, sans-serif',
                    borderRadius: '100px',
                    background: 'linear-gradient(135deg, #50E4F9, #00CED1)',
                    color: '#000000',
                    boxShadow: '0 0 20px rgba(80, 228, 249, 0.6)',
                  }}
                >
                  WIN IN LOTTERY
                </button>
                
                <button
                  onClick={async () => {
                    // Calculate total points earned
                    const totalPoints = (basePointsPerChest + inviteeBonus) * lastChestResult.chestsOpened + (lastChestResult.tierPointsEarned || 0);
                    const totalSolCredits = lastChestResult.totalPayoutSol;
                    try {
                      const result = await shareChest(totalPoints, totalSolCredits);
                      if (result.pointsEarned > 0) {
                        toast.success(`+${result.pointsEarned} Points!`, {
                          description: 'Share tracked successfully',
                        });
                      } else {
                        toast.info('Share opened', {
                          description: result.message,
                        });
                      }
                    } catch (error) {
                      toast.error('Failed to track share', {
                        description: error instanceof Error ? error.message : 'Unknown error',
                      });
                    }
                  }}
                  disabled={isSharing}
                  className="w-full sm:w-auto py-4 px-12 font-bold transition-all text-xl transform hover:scale-105 flex items-center justify-center gap-2 disabled:opacity-50"
                  style={{
                    fontFamily: 'Outfit, sans-serif',
                    borderRadius: '100px',
                    backgroundColor: 'transparent',
                    border: '2px solid #FFFFFF',
                    color: '#FFFFFF',
                  }}
                >
                  {getPotentialPoints('chest') > 0 ? (
                    <>
                      SHARE TO GET {getPotentialPoints('chest')} <img src="/points.svg" alt="Points" className="w-6 h-6" />
                    </>
                  ) : (
                    'SHARE ON X'
                  )}
                </button>
              </div>

              {/* Quit Button */}
              <div className="text-center">
                <button
                  onClick={() => setShowChestSuccess(false)}
                  className="text-white underline hover:opacity-70 transition-opacity"
                  style={{
                    fontFamily: 'Outfit, sans-serif',
                    fontSize: '18px',
                  }}
                >
                  QUIT
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
